import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, Upload } from "lucide-react";
import { toast } from "sonner";
import { getPhotos, uploadPhoto, photoUrl } from "../lib/api";
import { Reveal } from "./Reveal";

export const PhotoStrip = () => {
  const [photos, setPhotos] = useState([]);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const refresh = useCallback(() => {
    getPhotos().then((r) => setPhotos(r.photos)).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Pick a photo first");
      return;
    }
    setBusy(true);
    try {
      await uploadPhoto(file, name.trim());
      toast.success("Photo added to the strip — thank you!");
      setFile(null);
      setName("");
      if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — try a smaller photo");
    } finally {
      setBusy(false);
    }
  };

  const strip = photos.length >= 4 ? [...photos, ...photos] : photos;

  return (
    <section id="photos" className="relative bg-[#C88F52] px-6 md:px-16 lg:px-24 py-24 md:py-36 overflow-hidden" data-testid="photos-section">
      <Reveal x={80}>
        <p className="font-body text-[11px] uppercase tracking-[0.35em] text-forest mb-4">The Family Reel</p>
        <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-cream">
          Photos from our table — <span className="font-accent text-harvest text-4xl md:text-6xl align-middle -rotate-2 inline-block">add yours!</span>
        </h2>
        <p className="mt-5 font-body text-base md:text-lg text-fog leading-relaxed max-w-2xl">
          Fun family photos welcome — with soup or without. Past gatherings, kitchen disasters, the dog under the table. If it makes us smile, it belongs on the reel.
        </p>
      </Reveal>

      <Reveal delay={0.08} className="mt-12">
        <form onSubmit={submit} className="notice-block p-6 md:p-8 flex flex-col md:flex-row md:items-end gap-5" data-testid="photo-upload-form">
          <div className="flex-1">
            <label className="field-label" htmlFor="photo-name">Your Name</label>
            <input
              id="photo-name"
              data-testid="photo-name-input"
              className="field"
              placeholder="Who's sharing this gem?"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="field-label">Photo</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="photo-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <button
              type="button"
              data-testid="photo-pick-btn"
              onClick={() => fileRef.current?.click()}
              className="btn-ghost w-full !justify-start truncate"
            >
              <Camera size={16} className="shrink-0" />
              <span className="truncate">{file ? file.name : "Choose a photo…"}</span>
            </button>
          </div>
          <button data-testid="photo-upload-btn" type="submit" disabled={busy || !file} className="btn-ember shrink-0 disabled:opacity-40 disabled:cursor-not-allowed">
            <Upload size={16} />
            {busy ? "Adding…" : "Add to the Reel"}
          </button>
        </form>
      </Reveal>

      <Reveal delay={0.15} className="mt-12">
        {photos.length === 0 ? (
          <p className="font-accent text-3xl text-fog -rotate-1" data-testid="photos-empty">no photos yet — yours could be the very first!</p>
        ) : (
          <div className="overflow-hidden py-4" data-testid="photo-strip">
            <div className={photos.length >= 4 ? "marquee-track" : "flex flex-wrap gap-6"}>
              {strip.map((photo, i) => (
                <div
                  key={`${photo.id}-${i}`}
                  className={`shrink-0 mx-3 border-8 border-[#FBF4E6] bg-[#FBF4E6] shadow-xl shadow-forest/15 ${i % 2 ? "rotate-2" : "-rotate-2"}`}
                >
                  <img src={photoUrl(photo.id)} alt={photo.name ? `Photo shared by ${photo.name}` : "Family photo"} className="h-44 md:h-56 w-auto object-cover" />
                  {photo.name && <p className="font-accent text-xl text-forest text-center py-1">{photo.name}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </Reveal>
    </section>
  );
};
